import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { PAGE_NAMES } from 'appenv';

import { useSelector } from 'react-redux';
import { selectExhibitorsByKeyword } from 'models/exhibitors';
import { selectWebinarsByKeyword } from 'models/webinars';

import Footer from 'components/Footer';

import SearchFieldContainer from './SearchFieldContainer';
import AllSearchResultContainer from './AllSearchResultContainer';
import SearchResultContainer from './SearchResultContainer';

const StyledDialog = withStyles(() => ({
  paper: {
    backgroundColor: '#F0F1F2',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
}))(Dialog);

const HeaderContainer = styled.div`
  flex: 0 0 auto;
  width: 100%;
`;

const StyledTabsContainer = withStyles(() => ({
  root: {
    padding: '8px 21.5px',
    minHeight: '52px',
    '@media (max-width: 370px)': {
      padding: '8px 0',
    },
  },
  indicator: {
    backgroundColor: '#0CA58B',
    borderRadius: '100px',
    height: '100%',
    zIndex: 10,
  },
}))(Tabs);

const StyledTab = withStyles(() => ({
  root: {
    color: '#999',
    fontSize: '13px',
    fontWeight: 'bold',
    textTransform: 'none',
    flex: '1 0 auto',
    zIndex: 20,
    minHeight: 0,
    width: 'auto',
    letterSpacing: '0.09375px',
    lineHeight: '24px',
  },
  selected: {
    color: '#FFF',
  },
}))(Tab);

const BodyContainer = styled.div`
  flex: 1 1 auto;
  width: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

const TabContentContainer = styled.div`
  flex: 1 0 auto;
  width: 100%;
`;

const SearchModal = ({
  open,
  onClose,
}) => {
  const history = useHistory();
  const { location: { state } } = history;
  const [searchInputText, setSearchInputText] = useState((state && state.searchInputText) || '');
  const [activeTab, setActiveTab] = useState((state && state.activeTab) || 0);

  const [searchKeyword, setSearchKeyword] = useState(searchInputText);
  const debounceSetSearchKeyword = debounce(setSearchKeyword, 1000);
  const exhibitors = useSelector(selectExhibitorsByKeyword(searchKeyword));
  const webinars = useSelector(selectWebinarsByKeyword(searchKeyword));

  const onSeacrhInputChange = (event) => {
    history.replace({
      state: {
        ...state,
        searchInputText: event.target.value,
      },
    });
  };

  const onTabClick = (_, selectedTab) => {
    history.replace({
      state: {
        ...state,
        activeTab: selectedTab,
      },
    });
  };

  const swipeableViewsRef = React.createRef();
  useEffect(() => {
    if (swipeableViewsRef.current) {
      swipeableViewsRef.current.updateHeight();
    }
  }, [swipeableViewsRef, history, activeTab, exhibitors]);

  useEffect(() => {
    const unlisten = history.listen(({ state: newState }) => {
      const newSearchInputText = (newState && newState.searchInputText) || '';
      setSearchInputText(newSearchInputText);
      debounceSetSearchKeyword(newSearchInputText.length >= 2 ? newSearchInputText : '');
      setActiveTab((newState && newState.activeTab) || 0);
    });
    return unlisten;
  }, [debounceSetSearchKeyword, history]);

  const showResult = searchInputText !== '' && searchKeyword !== '';

  return (
    <StyledDialog
      fullScreen
      open={open}
      onClose={onClose}
      aria-labelledby="search-modal"
    >
      <HeaderContainer>
        <SearchFieldContainer
          searchText={searchInputText}
          onChange={onSeacrhInputChange}
          onCancel={onClose}
        />
        <StyledTabsContainer
          value={activeTab}
          onChange={onTabClick}
          variant="fullWidth"
          centered
        >
          <StyledTab label="All" />
          <StyledTab label={PAGE_NAMES.exhibitors} />
          <StyledTab label={PAGE_NAMES.webinar} />
        </StyledTabsContainer>
      </HeaderContainer>
      <BodyContainer>
        <TabContentContainer>
          {
            activeTab === 0 && (
              <AllSearchResultContainer
                showResult={showResult}
                exhibitors={exhibitors}
                webinars={webinars}
              />
            )
          }
          {
            activeTab === 1 && showResult && (
              <SearchResultContainer
                type="exhibitor"
                results={exhibitors}
              />
            )
          }
          {
            activeTab === 2 && showResult && (
              <SearchResultContainer
                type="webinar"
                results={webinars}
              />
            )
          }
        </TabContentContainer>
        <Footer hashState="/main-lobby" />
      </BodyContainer>
    </StyledDialog>
  );
};

SearchModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

SearchModal.defaultProps = {
  open: false,
  onClose: () => {},
};

export default SearchModal;
